import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import DownloadProductPricesPdfButton from "./DownloadProductPricesPdfButton";
import type { ProductFormState } from "./editProduct/ProductEdit";
import ProductEdit from "./editProduct/ProductEdit";
import ProductOptions from "./editProduct/ProductOptions";
import type { CreateOrUpdateProductRequest, ProductResponse } from "./productsApi";
import { useProduct } from "./useProduct";

interface BasicProductEditProps {
  readonly existingProduct: ProductFormState & { id: string; options: { id: string }[] };
  readonly onSubmit: (request: CreateOrUpdateProductRequest) => Promise<ProductResponse>;
  readonly onCancel: () => void;
  readonly onDelete: () => Promise<void>;
  readonly onDuplicate: () => Promise<void>;
}

const BasicProductEdit: FC<BasicProductEditProps> = ({
  existingProduct,
  onSubmit,
  onCancel,
  onDelete,
  onDuplicate,
}) => {
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const { control, handleSubmit, watch } = useForm<ProductFormState>({
    defaultValues: existingProduct,
    mode: "onBlur",
  });

  const productName = watch("name");
  const providerId = watch("provider.id");
  const submitDisabled = !productName || !providerId;
  return (
    <>
      <ProductEdit control={control} providerId={providerId} />
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <CancelButton onClick={onCancel} />
          <Button variant="outlined" color="error" onClick={() => setConfirmationDialogVisible(true)}>
            Usuń
          </Button>
          {existingProduct.options.length > 0 ? (
            <DownloadProductPricesPdfButton
              productId={existingProduct.id}
              productName={existingProduct.name}
              useDynamicPrices={existingProduct.provider.useDynamicPrices}
            />
          ) : null}
          <AsyncLoadingButton variant="outlined" color="secondary" onClick={onDuplicate}>
            Duplikuj
          </AsyncLoadingButton>
          <AsyncLoadingButton
            variant="outlined"
            color="primary"
            disabled={submitDisabled}
            onClick={handleSubmit(
              async ({
                name,
                provider,
                currency,
                info,
                description,
                taxNo,
                bankAccount,
                productsGroup,
                contact,
                images,
              }) => {
                await onSubmit({
                  name,
                  provider: {
                    id: provider.id,
                    productId: provider.productId,
                  },
                  currency,
                  info,
                  description,
                  taxNo: taxNo ? taxNo : undefined,
                  bankAccount: bankAccount ? bankAccount : undefined,
                  productsGroup: productsGroup ? productsGroup : undefined,
                  contact: {
                    address: contact.address ? contact.address : undefined,
                    email: contact.email ? contact.email : undefined,
                    googlePlaceId: contact.googlePlaceId ? contact.googlePlaceId : undefined,
                  },
                  images,
                });
              },
            )}
          >
            Zapisz
          </AsyncLoadingButton>
          <ConfirmationDialog
            open={confirmationDialogVisible}
            title="Usuń produkt"
            content="Czy chcesz usunąć produkt? Tej operacji nie będzie można już cofnąć."
            onCancel={() => setConfirmationDialogVisible(false)}
            onConfirm={onDelete}
          />
        </Stack>
      </Grid>
    </>
  );
};

const EditProductPage = () => {
  const navigate = useNavigate();
  const { product: existingProduct, isLoading, deleteProduct, cloneProduct, updateProduct } = useProduct();

  const handleDuplicate = async () => {
    const { id } = await cloneProduct();
    navigate(`/admin/products/${id}`);
  };

  const handleDelete = async () => {
    await deleteProduct();
    navigate("/admin/products");
  };

  const handleUpdateProduct = (update: CreateOrUpdateProductRequest) => {
    const { info, description, taxNo, bankAccount, productsGroup, contact } = update;
    return updateProduct({
      ...update,
      info: {
        external: info.external ? info.external : undefined,
        internal: info.internal ? info.internal : undefined,
      },
      description: description ?? {},
      taxNo: taxNo ? taxNo : undefined,
      bankAccount: bankAccount ? bankAccount : undefined,
      productsGroup: productsGroup ? productsGroup : undefined,
      contact: {
        address: contact.address ? contact.address : undefined,
        email: contact.email ? contact.email : undefined,
        googlePlaceId: contact.googlePlaceId ? contact.googlePlaceId : undefined,
      },
    });
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!existingProduct) {
    return (
      <Grid
        container
        spacing={2}
        sx={{
          marginTop: 1,
          marginBottom: 1,
        }}
      >
        <Typography
          sx={{
            margin: 2,
          }}
        >
          Taki produkt nie istnieje
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 1,
        marginBottom: 1,
        paddingLeft: 1,
        paddingRight: 1,
      }}
    >
      <BasicProductEdit
        existingProduct={existingProduct}
        onSubmit={handleUpdateProduct}
        onCancel={() => navigate("/admin/products")}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
      />
      <Grid size={12}>
        <ProductOptions
          productId={existingProduct.id}
          provider={existingProduct.provider}
          options={existingProduct.options}
        />
      </Grid>
    </Grid>
  );
};

export default EditProductPage;
